import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: {
      title: "Izvorna muzika - tekstovi pjesama",
      description:
        "Stranica posvećena izvornoj muzici s tekstovima pjesama, tradicijom i običajima Posavine i šire.",
    },
  },
  {
    path: "/import",
    name: "import",
    component: () => import("../views/import.vue"),
    meta: {
      title: "Import pjesama - Izvorna muzika",
      description: "Uvoz i administracija tekstova izvornih pjesama.",
    },
  },
  {
    path: "/Login",
    name: "Login",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Login.vue"),
    meta: {
      title: "Prijava - Izvorna muzika",
      description: "Prijava za administratore i urednike tekstova pjesama.",
    },
  },
  {
    path: "/oNama",
    name: "Onama",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/oNama.vue"),
    meta: {
      title: "O nama - Izvorna muzika",
      description:
        "Saznajte tko stoji iza stranice s tekstovima izvorne muzike i zašto čuvamo tradiciju i običaje.",
    },
  },
  {
    path: "/kontakt",
    name: "kontakt",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/kontakt.vue"),
    meta: {
      title: "Kontakt - Izvorna muzika",
      description:
        "Kontaktirajte Roberta Bekića za prijedloge, ispravke tekstova ili suradnju na izvornoj muzici.",
    },
  },
  {
    path: "/Signout",
    name: "Signout",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Signout.vue"),
    meta: {
      title: "Odjava - Izvorna muzika",
      description: "Odjava s administratorskog dijela stranice.",
      authRequired: true,
    },
  },
  {
    path: "/0-9",
    name: "0-9",
    component: () => import("../views/stranice/0-9.vue"),
    meta: {
      title: "Izvođači 0-9 - Izvorna muzika",
      description:
        "Popis izvođača izvorne muzike čija imena počinju brojevima.",
    },
  },
  {
    path: "/search/:query",
    name: "SearchResults",
    component: () => import("../views/SearchResults.vue"),
    props: true,
    meta: {
      title: "Pretraga - Izvorna muzika",
      description: "Rezultati pretrage tekstova izvornih pjesama.",
    },
  },
  {
    path: "/svi-izvodjaci",
    name: "SviIzvodjaci",
    component: () => import("@/views/AllSingers.vue"),
    meta: {
      title: "Svi izvođači - Izvorna muzika",
      description: "Popis svih izvođača izvorne muzike na stranici.",
    },
  },
  {
    path: "/sve-pjesme",
    name: "AllSongs",
    component: () => import("@/views/AllSongs.vue"),
    meta: {
      title: "Sve pjesme - Izvorna muzika",
      description: "Popis svih tekstova izvornih pjesama na stranici.",
    },
  },
];

const alphabet = "ABCČĆDĐEFGHIJKLMNOPRSŠTUVYZŽ";
alphabet.split("").forEach((letter) => {
  routes.push({
    path: `/stranice/${letter}`,
    name: `AlphabetPage_${letter}`,
    component:
      letter === "Č"
        ? () => import("../views/stranice/C_CARON.vue")
        : () => import(`../views/stranice/${letter}.vue`),
    props: true,
    meta: {
      title: `Izvođači na slovo ${letter} - Izvorna muzika`,
      description: `Popis izvođača izvorne muzike čije ime počinje slovom ${letter}.`,
    },
  });

  routes.push({
    path: `/stranice/${letter}/:singerName`,
    name: `SingerPage_${letter}`,
    component: () => import("../views/SingerPage.vue"),
    props: true,
    meta: {
      title: "Izvođač - Izvorna muzika",
      description: "Tekstovi pjesama određenog izvođača izvorne muzike.",
    },
  });

  routes.push({
    path: `/stranice/${letter}/:singerName/:songName`,
    name: `SongPage_${letter}`,
    component: () => import("../views/SongPage.vue"),
    props: true,
    meta: {
      title: "Tekst pjesme - Izvorna muzika",
      description: "Tekst pjesme izvorne muzike.",
    },
  });
});

routes.push({
  path: "/stranice/0-9/:singerName",
  name: "SingerPage_0-9",
  component: () => import("../views/SingerPage.vue"),
  props: true,
  meta: {
    title: "Izvođač 0-9 - Izvorna muzika",
    description: "Tekstovi pjesama izvođača izvorne muzike (0-9).",
  },
});

routes.push({
  path: "/stranice/0-9/:singerName/:songName",
  name: "SongPage_0-9",
  component: () => import("../views/SongPage.vue"),
  props: true,
  meta: {
    title: "Tekst pjesme 0-9 - Izvorna muzika",
    description: "Tekst pjesme izvođača izvorne muzike (0-9).",
  },
});

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

const DEFAULT_TITLE = "Izvorna muzika - tekstovi pjesama";
const DEFAULT_DESC =
  "Stranica posvećena izvornoj muzici s tekstovima pjesama i čuvanjem tradicije.";

router.afterEach((to) => {
  let title = (to.meta && to.meta.title) || DEFAULT_TITLE;
  let description = (to.meta && to.meta.description) || DEFAULT_DESC;

  if (String(to.name).startsWith("SingerPage_") && to.params.singerName) {
    const singer = to.params.singerName;
    title = `${singer} - tekstovi pjesama | Izvorna muzika`;
    description = `Tekstovi izvornih pjesama izvođača ${singer}.`;
  }

  if (String(to.name).startsWith("SongPage_") && to.params.songName) {
    const song = to.params.songName;
    const singer = to.params.singerName || "";
    title = `${song} - ${singer} | Tekst pjesme izvorne muzike`;
    description = `Tekst pjesme "${song}" izvođača ${singer} na stranici izvorne muzike.`;
  }

  document.title = title;

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.setAttribute("name", "description");
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute("content", description);
});

export default router;
